import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button, Input, FormField } from '../components/Form';
import { parseFieldErrors, parseErrorResponse } from '../utils/errorParser';

/**
 * 生成隨機 PIN 碼（固定 6 碼）
 * 使用 crypto.getRandomValues 確保更好的隨機性
 * 避免明顯模式（如 123456, 000000, 111111）
 */
function generateRandomPin(): string {
  // 使用 crypto.getRandomValues 獲取更好的隨機性（如果可用）
  const getRandomDigit = (): number => {
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const array = new Uint8Array(1);
      crypto.getRandomValues(array);
      return array[0] % 10;
    } else {
      // Fallback 到 Math.random
      return Math.floor(Math.random() * 10);
    }
  };

  // 生成 6 碼 PIN，避免明顯模式
  const blockedPatterns = [
    '123456',
    '000000',
    '111111',
    '222222',
    '333333',
    '444444',
    '555555',
    '666666',
    '777777',
    '888888',
    '999999',
  ];

  let pin: string;
  let attempts = 0;
  const maxAttempts = 10;

  do {
    // 生成 6 碼數字（補零）
    const digits: number[] = [];
    for (let i = 0; i < 6; i++) {
      digits.push(getRandomDigit());
    }
    pin = digits.join('').padStart(6, '0');
    attempts++;
  } while (attempts < maxAttempts && blockedPatterns.includes(pin));

  // 如果超過嘗試次數仍產生明顯模式，使用隨機替換
  if (blockedPatterns.includes(pin)) {
    const randomIndex = Math.floor(Math.random() * 6);
    const randomDigit = getRandomDigit();
    pin =
      pin.substring(0, randomIndex) +
      randomDigit +
      pin.substring(randomIndex + 1);
  }

  return pin;
}

/**
 * Staff 設定頁面（Setup 模式）
 * 用於擁有者第一次進來或被邀請者接受邀請後，第一次設定 staff 資訊
 *
 * 注意：此頁面僅用於 Setup（創建），不處理更新
 * - 如果已經有 staff，應該重定向到其他頁面或顯示錯誤
 *
 * 邏輯：
 * - 頁面載入時立即生成 PIN 碼並顯示（尚未存入資料庫）
 * - 點擊「儲存」時，將 PIN 碼發送到後端，後端加密後存入資料庫
 * - 儲存成功後自動導向後台管理頁面
 */
export function StaffSetup() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pinCode, setPinCode] = useState<string | null>(null); // 前端生成的 PIN 碼
  const [pinCodeError, setPinCodeError] = useState<string | null>(null); // PIN 碼欄位錯誤訊息
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    phone?: string;
    birthday?: string;
  }>({});
  const [formData, setFormData] = useState<{
    name: string;
    phone: string;
    birthday: string;
  }>({
    name: '',
    phone: '',
    birthday: '',
  });

  useEffect(() => {
    // 檢查 URL 參數中是否有 token（OAuth 回調可能直接導向此頁面）
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    const urlTenantId = urlParams.get('tenant_id') || urlParams.get('_tenant');

    if (urlToken && urlTenantId) {
      localStorage.setItem('tenant_token', urlToken);
      localStorage.setItem('tenant_id', urlTenantId);
      localStorage.removeItem('staff_session_token');
      localStorage.removeItem('staff_id');
      localStorage.removeItem('staff_name');
      window.dispatchEvent(new Event('localStorageChange'));

      // 清除 URL 參數（包括時間戳）
      window.history.replaceState({}, '', '/staff-setup');

      console.log('[StaffSetup] 從 URL 參數取得並儲存 token');
    }

    // 在遠端環境，給一點時間讓 Service Worker 和 localStorage 完全初始化
    const isRemote =
      window.location.hostname !== 'localhost' &&
      window.location.hostname !== '127.0.0.1';
    const delay = isRemote ? 200 : 50;

    setTimeout(() => {
      loadStaffInfo();
    }, delay);
  }, []);

  /**
   * 重新生成 PIN 碼（當 PIN 碼重複時使用）
   */
  const regeneratePin = () => {
    const newPin = generateRandomPin();
    setPinCode(newPin);
    setPinCodeError(null); // 清除錯誤訊息
    console.log('[StaffSetup] 重新生成 PIN 碼:', newPin);
  };

  /**
   * 載入當前用戶的 staff 資訊
   */
  const loadStaffInfo = async () => {
    try {
      const apiBase =
        import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

      // 在遠端環境，給更多時間讓 localStorage 完全寫入
      const isRemote =
        window.location.hostname !== 'localhost' &&
        window.location.hostname !== '127.0.0.1';
      const delay = isRemote ? 150 : 50;
      await new Promise(resolve => setTimeout(resolve, delay));

      // 重試邏輯：如果第一次沒有取得，再試一次
      let token = localStorage.getItem('tenant_token');
      let tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        console.warn('[StaffSetup] 第一次讀取失敗，重試...');
        await new Promise(resolve => setTimeout(resolve, 100));
        token = localStorage.getItem('tenant_token');
        tenantId = localStorage.getItem('tenant_id');
      }

      if (!token || !tenantId) {
        console.error('[StaffSetup] 缺少 token 或 tenant_id:', {
          hasToken: !!token,
          hasTenantId: !!tenantId,
          allKeys: Object.keys(localStorage),
        });
        setError('請先登入');
        setIsLoading(false);
        // 如果沒有 token，重定向到首頁讓用戶登入
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
        return;
      }

      console.log('[StaffSetup] 開始載入 staff 資訊:', {
        tokenLength: token.length,
        tenantId,
        isRemote,
      });

      const response = await fetch(`${apiBase}/api/staff/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          // Staff 不存在，這是 Setup 模式
          console.log('[StaffSetup] 404 錯誤，進入 Setup 模式');
          // 立即生成 PIN 碼並顯示（尚未存入資料庫，不檢查重複）
          const newPinCode = generateRandomPin();
          setPinCode(newPinCode);
          console.log('[StaffSetup] Setup 模式：已生成 PIN 碼:', newPinCode);
          // 嘗試從 member 資訊取得用戶名稱用於預填
          await loadUserInfoForPrefill();
        } else {
          const result = await response.json();
          setError(result.error || '載入員工資訊失敗');
        }
        setIsLoading(false);
        return;
      }

      const result = await response.json();
      if (result.success && result.data) {
        // Staff 已存在，重定向到設定頁面（不再進入編輯模式）
        console.log('[StaffSetup] Staff 已存在，重定向到設定頁面');
        window.location.href = '/admin?tab=tenant-settings';
        return;
      } else {
        setError('載入員工資訊失敗');
      }
    } catch (error) {
      console.error('載入員工資訊失敗:', error);
      setError(
        error instanceof Error ? error.message : '載入員工資訊時發生錯誤'
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 載入用戶資訊用於預填表單（創建模式時使用）
   */
  const loadUserInfoForPrefill = async () => {
    try {
      const apiBase =
        import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
      const token = localStorage.getItem('tenant_token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        return;
      }

      const response = await fetch(`${apiBase}/api/members/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data && result.data.user_name) {
          setFormData({
            name: result.data.user_name || '',
            phone: '',
            birthday: '',
          });
        }
      }
    } catch (error) {
      console.error('載入用戶資訊失敗:', error);
      // 不設置錯誤，因為這只是用於預填，失敗也不影響創建流程
    }
  };

  /**
   * 處理表單提交
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 清除之前的錯誤
    setFieldErrors({});
    setError(null);
    setPinCodeError(null);

    // 前端基本驗證
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) {
      errors.name = '請輸入姓名';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      toast.error('請檢查表單欄位');
      return;
    }

    setIsSaving(true);

    try {
      const apiBase =
        import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
      const token = localStorage.getItem('tenant_token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        toast.error('請先登入');
        setIsSaving(false);
        return;
      }

      // 創建模式：使用 POST /api/staff/me 創建
      if (!pinCode) {
        toast.error('PIN 碼未生成，請重新整理頁面');
        setIsSaving(false);
        return;
      }

      const createResponse = await fetch(`${apiBase}/api/staff/me`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          phone: formData.phone.trim() || undefined,
          birthday: formData.birthday.trim() || undefined,
          pin_code: pinCode, // 發送前端生成的 PIN 碼
        }),
      });

      const createResult = await createResponse.json();

      if (!createResponse.ok) {
        // 解析欄位級別的錯誤
        const parsedFieldErrors = parseFieldErrors(createResult);

        // 後端可能回傳 error（字串）或 message（ErrorResponse 格式），需先正規化為字串再判斷
        const errorStr =
          typeof createResult.error === 'string'
            ? createResult.error
            : typeof createResult.message === 'string'
              ? createResult.message
              : '';

        // 檢查是否為 PIN 碼重複錯誤（409 Conflict 或 400 Bad Request 且訊息包含 PIN）
        const isPinConflict =
          createResponse.status === 409 ||
          createResult.code === 'PIN_CODE_CONFLICT' ||
          (createResponse.status === 400 &&
            (errorStr.includes('PIN') ||
              errorStr.includes('pin') ||
              errorStr.includes('已被使用') ||
              errorStr.includes('已被其他員工使用')));

        if (isPinConflict) {
          // PIN 碼衝突：顯示錯誤並自動重新生成
          setPinCodeError('此 PIN 碼已被使用，請重新產生');
          regeneratePin(); // 自動重新生成 PIN 碼
          setIsSaving(false);
          return;
        }

        // 如果有欄位錯誤，設置欄位錯誤；否則顯示通用錯誤
        if (Object.keys(parsedFieldErrors).length > 0) {
          setFieldErrors(parsedFieldErrors);
        } else {
          const errorMessage = parseErrorResponse(createResult, {
            defaultMessage: '創建員工資訊失敗',
          });
          setError(errorMessage);
          toast.error(errorMessage);
        }
        setIsSaving(false);
        return; // 失敗時停留在原地，不跳轉
      }

      if (createResult.success) {
        toast.success('員工資訊已建立！');
        // 只有成功時才跳轉
        setTimeout(() => {
          window.location.href = '/admin?tab=tenant-settings';
        }, 1000);
      } else {
        throw new Error(
          parseErrorResponse(createResult, {
            defaultMessage: '創建員工資訊失敗',
          })
        );
      }
    } catch (error) {
      console.error('創建員工資訊失敗:', error);
      const errorMessage =
        error instanceof Error ? error.message : '創建員工資訊時發生錯誤';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsSaving(false);
      // 失敗時停留在原地，不跳轉
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-4">
            <svg
              className="w-16 h-16 text-red-500 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">載入失敗</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button
            variant="primary"
            onClick={() => {
              window.location.href = '/';
            }}
          >
            返回首頁
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            建立員工資訊
          </h1>
          <p className="text-gray-600">請填寫您的員工資訊</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <FormField label="姓名" required error={fieldErrors.name}>
            <Input
              type="text"
              value={formData.name}
              onChange={e => {
                setFormData({ ...formData, name: e.target.value });
                // 清除該欄位的錯誤
                if (fieldErrors.name) {
                  setFieldErrors({ ...fieldErrors, name: undefined });
                }
              }}
              placeholder="請輸入姓名"
              required
              disabled={isSaving}
            />
          </FormField>

          <FormField label="電話" error={fieldErrors.phone}>
            <Input
              type="tel"
              value={formData.phone}
              onChange={e => {
                setFormData({ ...formData, phone: e.target.value });
                // 清除該欄位的錯誤
                if (fieldErrors.phone) {
                  setFieldErrors({ ...fieldErrors, phone: undefined });
                }
              }}
              placeholder="請輸入電話（選填）"
              disabled={isSaving}
            />
          </FormField>

          <FormField label="生日" error={fieldErrors.birthday}>
            <Input
              type="date"
              value={formData.birthday}
              onChange={e => {
                setFormData({ ...formData, birthday: e.target.value });
                // 清除該欄位的錯誤
                if (fieldErrors.birthday) {
                  setFieldErrors({ ...fieldErrors, birthday: undefined });
                }
              }}
              disabled={isSaving}
            />
            <p className="text-xs text-gray-500 mt-1">生日為選填項目</p>
          </FormField>

          {/* 顯示 PIN 碼欄位（Setup 模式一定會顯示） */}
          <FormField label="PIN 碼" required error={pinCodeError || undefined}>
            {pinCode ? (
              // 顯示 PIN 碼（Setup 模式時立即生成並顯示）
              <div>
                <div
                  className={`bg-amber-50 border-2 rounded-lg p-4 ${pinCodeError ? 'border-red-300' : 'border-amber-300'}`}
                >
                  <div className="text-center">
                    <div className="bg-amber-100 rounded-lg p-3 border-2 border-amber-400 mb-2">
                      <p className="text-2xl font-bold text-amber-900 font-mono tracking-wider">
                        {pinCode}
                      </p>
                    </div>
                    <p className="text-sm font-medium text-amber-800 mb-1">
                      此為登入POS系統的pin code，請好好保管
                    </p>
                    {pinCodeError && (
                      <p className="text-xs text-red-600 mt-2">
                        {pinCodeError}
                      </p>
                    )}
                  </div>
                </div>
                {pinCodeError && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={regeneratePin}
                    className="mt-2 w-full"
                  >
                    重新產生 PIN 碼
                  </Button>
                )}
              </div>
            ) : (
              // PIN 碼尚未生成（可能正在生成中）
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-600 text-center">
                  正在生成 PIN 碼...
                </p>
              </div>
            )}
          </FormField>

          {/* 儲存按鈕 */}
          <div className="flex gap-4 pt-4">
            <Button
              type="submit"
              variant="primary"
              loading={isSaving}
              disabled={isSaving}
              className="w-full"
            >
              {isSaving ? '儲存中...' : '儲存'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
